import { getBaseUrl } from '@pique/graphql';

import type {
  ImageSearchOptions,
  ImageSearchResult,
  ImageSource,
} from './types';

interface VectorResult {
  id: string;
  title: string;
  url: string;
}

interface VectorResponse {
  results: VectorResult[];
}

export class UndrawSource implements ImageSource {
  readonly name = 'undraw';
  private readonly searchCache = new Map<string, { results: ImageSearchResult[]; timestamp: number }>();
  private readonly CACHE_DURATION = 300000; // 5 minutes

  async search(options: ImageSearchOptions): Promise<ImageSearchResult[]> {
    // Check cache first
    const cacheKey = `${options.query}_${options.width}_${options.height}`;
    const cached = this.searchCache.get(cacheKey);
    if (cached && (Date.now() - cached.timestamp) < this.CACHE_DURATION) {
      return cached.results;
    }

    const url = new URL(getBaseUrl() + '/api/copilot/vectors/search');
    url.searchParams.set('term', options.query);

    try {
      const response = await fetch(url.toString(), {
        signal: AbortSignal.timeout(10000), // 5 second timeout
      });
      
      if (!response.ok) {
        console.warn(`Undraw API returned ${response.status}, will try fallback`);
        return this.tryAlternativeQueries(options);
      }

      const result: VectorResponse = await response.json();
      
      if (!result.results || result.results.length === 0) {
        return this.tryAlternativeQueries(options);
      }
      
      // Return results in order - first result is most relevant
      const searchResults = result.results.map(r => ({
        url: r.url,
        id: r.id,
        title: r.title,
        type: 'svg' as const,
      }));

      // Cache the results
      this.searchCache.set(cacheKey, { results: searchResults, timestamp: Date.now() });

      return searchResults;
    } catch (error) {
      console.warn('Error fetching vector illustrations, trying alternatives:', error);
      return this.tryAlternativeQueries(options);
    }
  }

  private async tryAlternativeQueries(options: ImageSearchOptions): Promise<ImageSearchResult[]> {
    // Try simplified queries if the original fails
    const alternatives = this.generateAlternativeQueries(options.query);

    for (const altQuery of alternatives) {
      try {
        const url = new URL(getBaseUrl() + '/api/copilot/vectors/search');
        url.searchParams.set('term', altQuery);
        
        const response = await fetch(url.toString(), {
          signal: AbortSignal.timeout(3000),
        });
        
        if (response.ok) {
          const result = await response.json() as { results?: VectorResult[] };
          if (result.results && result.results.length > 0) {
            return result.results.map((r: VectorResult) => ({
              url: r.url,
              id: r.id,
              title: r.title,
              type: 'svg' as const,
            }));
          }
        }
      } catch (error) {
        // Continue to next alternative
      }
    }
    
    return [];
  }
  
  private generateAlternativeQueries(query: string): string[] {
    const alternatives: string[] = [];
    const words = query.toLowerCase().split(/\s+/);

    const firstWord = words[0];
    const lastWord = words[words.length - 1];

    if (words.length > 1 && firstWord && firstWord.length > 2) {
      alternatives.push(firstWord);
    }

    if (words.length > 1 && lastWord && lastWord.length > 2) {
      alternatives.push(lastWord);
    }

    // Try common synonyms for common terms
    const synonymMap: Record<string, string[]> = {
      'conference': ['meeting', 'presentation', 'speaker'],
      'networking': ['collaboration', 'team', 'connection'],
      'innovation': ['idea', 'creative', 'startup'],
      'community': ['team', 'group', 'people'],
      'technology': ['tech', 'computer', 'digital'],
      'business': ['work', 'office', 'professional'],
    };

    for (const word of words) {
      const synonyms = synonymMap[word];
      if (synonyms) {
        alternatives.push(...synonyms);
      }
    }

    // Remove duplicates and limit to 3 alternatives
    return [...new Set(alternatives)].slice(0, 3);
  }
}
